import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useSpring, animated } from 'react-spring'

import { Table } from 'nswidgets/src/components/table/table'

import { useStyles } from './chart-flipper.style'
import { DownloadLink } from '../../common/components/download-link'
import { csvExportService } from './csv-export.service'

export const ChartFlipper = function ({
  children,
  enabled = true,
  showTable = false,
  tableData,
  tableProps,
  dataKey,
  selectedTableRowKey,
  isHalf = false,
  tableStyles = {},
  tableOffset = 0,
  chartSideStyles = {},
  tableSideStyles = {}
}) {
  const classes = useStyles()

  const selectedRows = (selectedTableRowKey !== undefined) ? new Set([selectedTableRowKey]) : undefined
  const containerDimensionsRef = useRef({})
  const [containerDimensions, setContainerDimensions] = useState({})
  const [isTableScrollEnd, setIsTableScrollEnd] = useState(false)
  const chartAnimatedDivRef = useRef()
  const tableAnimatedDivRef = useRef()
  const previouslySelectedRow = useRef()
  const previousTableDataLength = useRef(0)

  const containerHeight = (containerDimensionsRef.current.height || 0) - tableOffset

  const tableContainerRef = useCallback((node) => {
    if (node) {
      runTableScrollEndCheck(node)

      if ((selectedTableRowKey !== undefined) && (selectedTableRowKey !== previouslySelectedRow.current)) {
        const row = node.querySelector(`[data-row-key="${selectedTableRowKey}"]`)
        if (row) {
          // Position the previous row under the heading so you can see before & after without scrolling
          const ROW_HEIGHT = 27
          node.scroll({ top: row.offsetTop - (ROW_HEIGHT * 2) })
          previouslySelectedRow.current = selectedTableRowKey
        }
      }

      if (tableData.rows.length !== previousTableDataLength.current) {
        node.scroll({ top: 0 })
        previousTableDataLength.current = tableData.rows.length
      }
    }
    tableContainerRef.current = node
  }, [ selectedTableRowKey, tableData ])

  useEffect(() => {
    if (showTable) {
      runTableScrollEndCheck(tableContainerRef.current)
    }
  }, [showTable])

  const { transform, opacity } = useSpring({
    opacity: showTable ? 1 : 0,
    transform: `rotateY(${showTable ? 180 : 0}deg)`,
    config: { mass: 1, tension: 500, friction: 80 },
    onStart: () => {
      if (!chartAnimatedDivRef.current || !tableAnimatedDivRef.current) {
        return
      }

      chartAnimatedDivRef.current.style.pointerEvents = 'none'
      tableAnimatedDivRef.current.style.pointerEvents = 'none'
    },
    onRest: () => {
      if (!chartAnimatedDivRef.current || !tableAnimatedDivRef.current) {
        return
      }

      chartAnimatedDivRef.current.style.pointerEvents = showTable ? 'none' : ''
      tableAnimatedDivRef.current.style.pointerEvents = showTable ? '' : 'none'
    }
  })

  function onTableScroll (event) {
    if (event.target) {
      runTableScrollEndCheck(event.target)
    }
  }

  function runTableScrollEndCheck (element) {
    const SCROLL_END_THRESHOLD_AMOUNT = 2 // Offset the check by a couple pixels to account for minor differences in scroll heights between different browsers and screen resolutions
    const { offsetHeight, scrollTop, scrollHeight } = element
    if (!isTableScrollEnd && ((offsetHeight + scrollTop + SCROLL_END_THRESHOLD_AMOUNT >= scrollHeight) || (offsetHeight + tableOffset < (containerDimensionsRef.current.height || 0)))) {
      setIsTableScrollEnd(true)
    } else if (isTableScrollEnd && (offsetHeight + scrollTop + SCROLL_END_THRESHOLD_AMOUNT < scrollHeight)) {
      setIsTableScrollEnd(false)
    }
  }

  function chartContainerRef (div) {
    if (!div) {
      return
    }

    if ((containerDimensions.width !== div.clientWidth) || (containerHeight !== div.clientHeight - tableOffset)) {
      containerDimensionsRef.current = {
        width: div.clientWidth,
        height: div.clientHeight
      }
      setContainerDimensions(containerDimensionsRef.current)
    }
  }

  if (!enabled) {
    return children
  }

  return <div className={classes.container} style={{ ...containerDimensions }}>
    <animated.div
      className={classes.side}
      style={{ opacity: opacity.interpolate((o) => 1 - o), transform, ...chartSideStyles }}
      ref={chartAnimatedDivRef}
      data-qa="chart-flipper-chart-side"
    >
      <div ref={chartContainerRef}>
        {children}
      </div>
    </animated.div>
    { tableData && (
      <animated.div
        className={classes.side}
        style={{ opacity, transform: transform.interpolate((t) => `${t} rotateY(180deg)`), ...tableSideStyles }}
        ref={tableAnimatedDivRef}
        data-qa="chart-flipper-table-side"
      >
        {!isHalf && (
          <div>
            <DownloadLink contentType="text/csv" fileName={`${dataKey}.csv`} className={classes.downloadLink} data={csvExportService.mapChartDataToCsv(tableData)}/>
          </div>
        )}
        <div className={classes.table} ref={tableContainerRef} onScroll={onTableScroll} style={{ maxHeight: containerHeight, ...tableStyles }} data-qa="chart-flipper-table">
          <Table tableData={tableData} selectedRows={selectedRows} {...tableProps} enableVirtualScrolling={true} isHalf={isHalf} />
        </div>
        { !isTableScrollEnd && <div className={classes.tableBottomFade} /> }
        {isHalf && (
          <div>
            <DownloadLink contentType="text/csv" fileName={`${dataKey}.csv`} className={classes.downloadLink} data={csvExportService.mapChartDataToCsv(tableData)}/>
          </div>
        )}
      </animated.div>
    )}
  </div>
}
